import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  MenuBook as MenuBookIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaContabilidad,
} from 'lib/seguridad/permisosPorModulo';
import { FP, fpInputSx } from 'components/financepro';
import {
  PROGRAMA_ENTRADAS_DIARIO,
  TIPO_DOCUMENTO_AJUSTE_ENTRADAS,
  fmtMoney,
  fechaLocalHoy,
  labelCuentaCatalogo,
  labelCentroCosto,
  cuentasPosteablesDeCatalogo,
  wanomesDesdeAnomesContabilidad,
  validarMesContableAbierto,
  ladoDesdeOrigenCuenta,
  cellHeadSx,
  btnPrimarySx,
  btnOutlinedSx,
} from './entradaDiarioShared';

let lineaSeq = 1;

const lineaVaciaDesdeCuenta = (cuenta, lado, monto, centro, conceptoLinea, conceptoGeneral) => {
  const valor = parseFloat(String(monto).replace(',', '.')) || 0;
  const ladoN = lado === 'Credito' ? 'Credito' : 'Debito';
  return {
    id: lineaSeq++,
    cuenta: cuenta?.cuenta || '',
    nombre: cuenta?.nombre || '',
    origenCuenta: cuenta?.origen || '',
    lado: ladoN,
    monto: valor,
    debito: ladoN === 'Debito' ? valor : 0,
    credito: ladoN === 'Credito' ? valor : 0,
    centrodecosto: centro?.id ?? null,
    centroNombre: centro ? labelCentroCosto(centro) : '',
    concepto: (conceptoLinea || conceptoGeneral || '').trim(),
  };
};

/**
 * Formulario reutilizable para registrar un asiento de Ajuste Entradas.
 * Se usa como diálogo (open/onClose) desde el resumen u otras pantallas.
 */
const RegistrarEntradaDiario = ({ open = false, onClose, onGuardado }) => {
  const { controller, selectedCompany, showSuccess, showError, showWarning } =
    useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA_ENTRADAS_DIARIO);
  const puedeModificar = puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_ENTRADAS_DIARIO);
  const puedeOperarDb = Boolean(selectedCompany?.db_empresa);

  const [fecha, setFecha] = useState(fechaLocalHoy);
  const [concepto, setConcepto] = useState('');
  const [tasa, setTasa] = useState('1');
  const [lineas, setLineas] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const [cuentasCatalogo, setCuentasCatalogo] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [cargandoCatalogo, setCargandoCatalogo] = useState(false);
  const [cargandoCentros, setCargandoCentros] = useState(false);
  const [wanomesContableAbierto, setWanomesContableAbierto] = useState(null);

  const [cuentaSel, setCuentaSel] = useState(null);
  const [lado, setLado] = useState('Debito');
  const [montoStr, setMontoStr] = useState('');
  const [centroSel, setCentroSel] = useState(null);
  const [conceptoLinea, setConceptoLinea] = useState('');

  const cuentasPosteables = useMemo(
    () => cuentasPosteablesDeCatalogo(cuentasCatalogo),
    [cuentasCatalogo]
  );

  const wanomesDesdeLogin = useMemo(
    () => wanomesDesdeAnomesContabilidad(selectedCompany?.anomescontabilidad),
    [selectedCompany?.anomescontabilidad]
  );

  const validacionFecha = useMemo(
    () => validarMesContableAbierto(fecha, wanomesContableAbierto),
    [fecha, wanomesContableAbierto]
  );

  const totalDebito = useMemo(
    () => lineas.reduce((s, l) => s + (parseFloat(l.debito) || 0), 0),
    [lineas]
  );
  const totalCredito = useMemo(
    () => lineas.reduce((s, l) => s + (parseFloat(l.credito) || 0), 0),
    [lineas]
  );
  const diferencia = Math.round((totalDebito - totalCredito) * 100) / 100;
  const cuadrada = lineas.length >= 2 && Math.abs(diferencia) < 0.01 && totalDebito > 0;

  const resetFormulario = useCallback(() => {
    setFecha(fechaLocalHoy());
    setConcepto('');
    setTasa('1');
    setLineas([]);
    setCuentaSel(null);
    setLado('Debito');
    setMontoStr('');
    setCentroSel(null);
    setConceptoLinea('');
  }, []);

  const credencialesBase = useCallback(
    () => ({
      id_empresa: selectedCompany,
      usuario: usuarioLog || '',
      password: claveLog || '',
    }),
    [selectedCompany, usuarioLog, claveLog]
  );

  const cargarCatalogo = useCallback(async () => {
    if (!puedeOperarDb) return;
    setCargandoCatalogo(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarcatalogoactualizable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...credencialesBase(), busqueda: '', orden: 'cuenta' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo cargar el catálogo de cuentas');
        setCuentasCatalogo([]);
        return;
      }
      setCuentasCatalogo(data.cuentas || []);
    } catch {
      showError('Error de conexión al cargar el catálogo');
      setCuentasCatalogo([]);
    } finally {
      setCargandoCatalogo(false);
    }
  }, [puedeOperarDb, servidor, credencialesBase, showError]);

  const cargarCentros = useCallback(async () => {
    if (!puedeOperarDb) return;
    setCargandoCentros(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarcentrodecosto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credencialesBase()),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setCentrosCosto([]);
        return;
      }
      setCentrosCosto(data.centros || []);
    } catch {
      setCentrosCosto([]);
    } finally {
      setCargandoCentros(false);
    }
  }, [puedeOperarDb, servidor, credencialesBase]);

  const cargarVarinicial = useCallback(async () => {
    if (!puedeOperarDb) return;
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_obtenervarinicialcont`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credencialesBase()),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.wanomes) {
        setWanomesContableAbierto(String(data.wanomes));
      } else if (!wanomesDesdeLogin) {
        setWanomesContableAbierto(null);
        if (data.Error) showWarning(data.Error);
      }
    } catch {
      if (!wanomesDesdeLogin) setWanomesContableAbierto(null);
    }
  }, [puedeOperarDb, servidor, credencialesBase, wanomesDesdeLogin, showWarning]);

  useEffect(() => {
    if (!open) return;
    resetFormulario();
    if (wanomesDesdeLogin) setWanomesContableAbierto(wanomesDesdeLogin);
    if (puedeLeer && puedeOperarDb) {
      cargarCatalogo();
      cargarCentros();
      if (!wanomesDesdeLogin) cargarVarinicial();
    }
  }, [
    open,
    puedeLeer,
    puedeOperarDb,
    wanomesDesdeLogin,
    cargarCatalogo,
    cargarCentros,
    cargarVarinicial,
    resetFormulario,
  ]);

  const onSeleccionarCuenta = (cuenta) => {
    setCuentaSel(cuenta);
    if (cuenta) setLado(ladoDesdeOrigenCuenta(cuenta.origen));
  };

  const agregarLinea = () => {
    if (!puedeModificar) {
      showError('No tiene permisos para registrar asientos.');
      return;
    }
    if (!cuentaSel?.cuenta) {
      showError('Seleccione una cuenta contable');
      return;
    }
    const monto = parseFloat(String(montoStr).replace(',', '.')) || 0;
    if (monto <= 0) {
      showError('Indique un monto mayor que cero');
      return;
    }
    setLineas((prev) => [
      ...prev,
      lineaVaciaDesdeCuenta(cuentaSel, lado, monto, centroSel, conceptoLinea, concepto),
    ]);
    setCuentaSel(null);
    setMontoStr('');
    setCentroSel(null);
    setConceptoLinea('');
    setLado('Debito');
  };

  const eliminarLinea = (id) => {
    if (!puedeModificar) return;
    setLineas((prev) => prev.filter((l) => l.id !== id));
  };

  const guardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permisos para registrar asientos.');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!validacionFecha.ok) {
      showError(validacionFecha.message);
      return;
    }
    if (!concepto.trim()) {
      showError('El concepto es requerido');
      return;
    }
    if (lineas.length < 2) {
      showError('El asiento debe tener al menos dos líneas');
      return;
    }
    if (!cuadrada) {
      showError(
        `El asiento no está cuadrado (débito $${fmtMoney(totalDebito)} ≠ crédito $${fmtMoney(totalCredito)})`
      );
      return;
    }

    setGuardando(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_guardarentradadiario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          documento: TIPO_DOCUMENTO_AJUSTE_ENTRADAS,
          fecha,
          concepto: concepto.trim(),
          tasa,
          lineas: lineas.map((l) => ({
            cuenta: l.cuenta,
            debito: l.debito,
            credito: l.credito,
            centrodecosto: l.centrodecosto,
            concepto: l.concepto || concepto.trim(),
          })),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo guardar el asiento');
        return;
      }
      showSuccess(data.mensaje || 'Asiento registrado');
      if (onGuardado) onGuardado(data.entrada || {});
      if (onClose) onClose();
    } catch {
      showError('Error de conexión al guardar el asiento');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={guardando ? undefined : onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
          overflow: 'hidden',
          width: { xs: '96vw', md: '94vw' },
          maxWidth: 1480,
          height: { xs: '92vh', md: '90vh' },
          maxHeight: { xs: '92vh', md: '90vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2.5, md: 4 },
          py: { xs: 2.2, md: 2.6 },
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.4,
            bgcolor: FP.color.accentBlueSoft,
            color: FP.color.accentBlue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <MenuBookIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '1.15rem', letterSpacing: '-0.01em' }}>
            Nueva entrada de diario
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.86rem', mt: 0.35 }}>
            Documento {TIPO_DOCUMENTO_AJUSTE_ENTRADAS} · debe = haber
          </Typography>
        </Box>
        <Chip
          size="small"
          label={cuadrada ? 'Cuadrada' : `Dif. $${fmtMoney(Math.abs(diferencia))}`}
          sx={{
            fontWeight: 700,
            bgcolor: cuadrada ? FP.color.successSoft : FP.color.warningSoft,
            color: cuadrada ? FP.color.successDark : FP.color.navy,
          }}
        />
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2.5, md: 4 },
          pb: { xs: 2.5, md: 3 },
          bgcolor: FP.color.surface,
          '&.MuiDialogContent-root': {
            paddingTop: { xs: 3, md: 4.5 },
          },
        }}
      >
        {!puedeOperarDb && (
          <Alert severity="info" sx={{ mb: 2.5 }}>
            Seleccione una empresa con base de datos para registrar asientos.
          </Alert>
        )}

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              label="Tipo de documento"
              size="small"
              value={TIPO_DOCUMENTO_AJUSTE_ENTRADAS}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              label="Fecha"
              type="date"
              size="small"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={!validacionFecha.ok}
              helperText={!validacionFecha.ok ? validacionFecha.message : ' '}
              disabled={!puedeModificar}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              fullWidth
              label="Tasa"
              size="small"
              value={tasa}
              onChange={(e) => setTasa(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              disabled={!puedeModificar}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Concepto"
              size="small"
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              disabled={!puedeModificar}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          </Grid>
        </Grid>

        <Typography sx={{ fontWeight: 700, mt: 3.5, mb: 0.8, color: FP.color.textPrimary, fontSize: '1.02rem' }}>
          Líneas del asiento
        </Typography>
        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem', mb: 2 }}>
          Al seleccionar la cuenta se propone débito o crédito según su origen; puede cambiarlo. El
          asiento no se guarda hasta que los totales coincidan.
        </Typography>

        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={cuentasPosteables}
              loading={cargandoCatalogo}
              value={cuentaSel}
              onChange={(_, v) => onSeleccionarCuenta(v)}
              getOptionLabel={(o) => labelCuentaCatalogo(o)}
              isOptionEqualToValue={(a, b) => String(a?.cuenta) === String(b?.cuenta)}
              disabled={!puedeModificar}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cuenta contable"
                  placeholder="Código o nombre"
                  size="small"
                  sx={fpInputSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              fullWidth
              label="Afecta"
              size="small"
              value={lado}
              onChange={(e) => setLado(e.target.value)}
              disabled={!puedeModificar}
              sx={fpInputSx}
            >
              <MenuItem value="Debito">Débito</MenuItem>
              <MenuItem value="Credito">Crédito</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              fullWidth
              label="Monto"
              size="small"
              value={montoStr}
              onChange={(e) => setMontoStr(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              disabled={!puedeModificar}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Autocomplete
              options={centrosCosto}
              loading={cargandoCentros}
              value={centroSel}
              onChange={(_, v) => setCentroSel(v)}
              getOptionLabel={(o) => labelCentroCosto(o)}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              disabled={!puedeModificar}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Centro de costo"
                  placeholder="Localidad o sucursal"
                  size="small"
                  sx={fpInputSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={agregarLinea}
              disabled={!puedeModificar || !puedeOperarDb}
              sx={btnOutlinedSx}
            >
              Añadir
            </Button>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Concepto de la línea (opcional)"
              size="small"
              value={conceptoLinea}
              onChange={(e) => setConceptoLinea(e.target.value)}
              disabled={!puedeModificar}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Cuentas del asiento
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', fontWeight: 600 }}>
              {lineas.length} {lineas.length === 1 ? 'línea' : 'líneas'}
            </Typography>
          </Box>
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            {lineas.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                <ReceiptLongIcon sx={{ fontSize: 52, color: FP.color.textMuted, mb: 1.5 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay cuentas para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Seleccione una cuenta, el monto y pulse «Añadir». Debe haber al menos una línea al débito y otra al crédito.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 980, sm: 1120 } }}>
                  <colgroup>
                    {['12%', '20%', '22%', '16%', '10%', '10%', '10%'].map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Cuenta
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Nombre
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Concepto
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Centro
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Débito
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Crédito
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {lineas.map((ln) => (
                      <TableRow key={ln.id} hover>
                        <TableCell
                          sx={{ fontWeight: 700, color: FP.color.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={ln.cuenta || '—'}
                        >
                          {ln.cuenta || '—'}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={ln.nombre || '—'}
                        >
                          {ln.nombre || '—'}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={ln.concepto || '—'}
                        >
                          {ln.concepto || '—'}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={ln.centroNombre || '—'}
                        >
                          {ln.centroNombre || '—'}
                        </TableCell>
                        <TableCell align="right">
                          {ln.debito > 0 ? `$${fmtMoney(ln.debito)}` : ''}
                        </TableCell>
                        <TableCell align="right">
                          {ln.credito > 0 ? `$${fmtMoney(ln.credito)}` : ''}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Eliminar línea">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => eliminarLinea(ln.id)}
                                disabled={!puedeModificar}
                                sx={{
                                  color: FP.color.danger,
                                  '&:hover': { bgcolor: FP.color.dangerSoft },
                                  '&.Mui-disabled': { color: FP.color.textMuted },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{
                        '& td': {
                          borderTop: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                          fontWeight: 800,
                        },
                      }}
                    >
                      <TableCell colSpan={4}>Totales</TableCell>
                      <TableCell align="right">${fmtMoney(totalDebito)}</TableCell>
                      <TableCell align="right">${fmtMoney(totalCredito)}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2.5, md: 4 },
          py: 2,
          borderTop: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          startIcon={<CloseIcon />}
          disabled={guardando}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: FP.color.textSecondary,
            borderRadius: 1.4,
            '&:hover': { bgcolor: FP.color.surfaceAlt },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={guardar}
          disabled={!puedeModificar || !usuarioLog || !puedeOperarDb || guardando || !cuadrada}
          sx={btnPrimarySx}
        >
          Guardar asiento
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrarEntradaDiario;
